import { createRouter, createWebHistory } from 'vue-router'
import { estaAutenticado } from '@/services/servicioAutenticacion'
import almacen from '@/services/almacen'

// Layout principal
import AdminLayout from '@/components/AdminLayout.vue'

// Panel principal
import AdminDashboard from '@/views/dashboard/AdminDashboard.vue'

// Gestión de empresas
import EmpresasList from '@/views/empresas/EmpresasList.vue'
import EmpresasCrear from '@/views/empresas/EmpresasCrear.vue'
import EmpresasSubdominios from '@/views/empresas/EmpresasSubdominios.vue'

// Gestión de proveedores
import ProveedoresList from '@/views/proveedores/ProveedoresList.vue'
import ProveedorCrear from '@/views/proveedores/ProveedorCrear.vue'

// Usuarios administradores
import UsuariosList from '@/views/usuarios/UsuariosList.vue'
import UsuarioCrear from '@/views/usuarios/UsuarioCrear.vue'
import RolesPermisos from '@/views/usuarios/RolesPermisos.vue'
import AdminLoginView from '@/views/usuarios/Login.vue'

// Gestión de módulos del sistema
import ModulosGestion from '@/views/modulos/ModulosGestion.vue'
import ModulosPermisos from '@/views/modulos/ModulosPermisos.vue'
import ModulosActivacion from '@/views/modulos/ModulosActivacion.vue'
import ModulosEmpresaAsignacion from '@/views/modulos/ModulosEmpresaAsignacion.vue'

// Facturación del sistema (suscripciones, pagos)
import SuscripcionesList from '@/views/facturacion/SuscripcionesList.vue'
import PagosList from '@/views/facturacion/PagosList.vue'
import PlanesList from '@/views/facturacion/PlanesList.vue'
import FacturacionPlanes from '@/views/facturacion/FacturacionPlanes.vue'

// Reportes centrales
import ReportesEmpresas from '@/views/reportes/ReportesEmpresas.vue'
import ReportesIngresos from '@/views/reportes/ReportesIngresos.vue'
import ReportesUso from '@/views/reportes/ReportesUso.vue'

// Configuraciones centrales
import AdminConfiguracionUsuarios from '@/views/configuraciones/AdminConfiguracionUsuarios.vue'
import AdminConfiguracionParametros from '@/views/configuraciones/AdminConfiguracionParametros.vue'
import EmpresaPrincipal from '@/views/configuraciones/EmpresaPrincipal.vue'
import SeguridadDosFactores from '@/views/configuraciones/SeguridadDosFactores.vue'
import ConfiguracionSri from '@/views/sri/ConfiguracionSri.vue'

// Control de acceso (modulos, planes, suscripciones)
import ControlAccesoModulos from '@/views/control-acceso/ControlAccesoModulos.vue'
import ControlAccesoPlanes from '@/views/control-acceso/ControlAccesoPlanes.vue'
import ControlAccesoSuscripciones from '@/views/control-acceso/ControlAccesoSuscripciones.vue'
import ComprobantesAdmin from '@/views/comprobantes/ComprobantesAdmin.vue'

// Notificaciones del sistema (banner en Facturacion del portal cliente)
import AdminNotificaciones from '@/views/notificaciones/AdminNotificaciones.vue'

// Infraestructura Hostinger (nodos/datacenters y cron jobs)
import AdminInfraestructura from '@/views/infraestructura/AdminInfraestructura.vue'

const routes = [
  { path: '/iniciar-sesion', name: 'InicioSesionAdmin', component: AdminLoginView },
  // Ruta temporal sin guardia para pruebas
  { path: '/iniciar-sesion-prueba', name: 'InicioSesionPrueba', component: AdminLoginView, meta: { omitirGuardia: true } },
  // Ruta de depuración para forzar acceso al inicio de sesión
  { path: '/depuracion/iniciar-sesion', name: 'DepuracionInicioSesion', component: AdminLoginView },
  // Página de depuración para verificar estado de autenticación
  { path: '/depuracion/autenticacion', name: 'DepuracionAutenticacion', component: () => import('@/views/debug/AuthDebug.vue') },
  // Página de inicio de sesión simplificada para pruebas
  { path: '/iniciar-sesion-simple', name: 'InicioSesionSimple', component: () => import('@/views/SimpleLogin.vue') },
  // Página de prueba para verificar funcionamiento básico
  { path: '/prueba', name: 'PaginaPrueba', component: () => import('@/views/TestPage.vue') },
  // Redirección legacy
  { path: '/login', redirect: '/iniciar-sesion' },
  // Página de inicio y layout principal del panel
  {
    path: '/',
    component: AdminLayout,
    meta: { requiresAuth: true },
    children: [
      // Dashboard
      // Dashboard Central
      { path: '', name: 'Dashboard', component: AdminDashboard, meta: { requiresAuth: true } },

      // Gestión de Empresas
      { path: 'empresas', name: 'EmpresasList', component: EmpresasList, meta: { requiresAuth: true } },
      { path: 'empresas/nueva', name: 'EmpresasCrear', component: EmpresasCrear, meta: { requiresAuth: true } },
      { path: 'empresas/editar/:id', name: 'EmpresasEditar', component: EmpresasCrear, meta: { requiresAuth: true } },
      { path: 'empresas/subdominios', name: 'EmpresasSubdominios', component: EmpresasSubdominios, meta: { requiresAuth: true } },
      { path: 'empresas/usuarios', name: 'EmpresasUsuarios', component: () => import('@/views/empresas/EmpresasUsuarios.vue'), meta: { requiresAuth: true } },

      // Gestión de Proveedores
      { path: 'proveedores', name: 'ProveedoresList', component: ProveedoresList, meta: { requiresAuth: true } },
      { path: 'proveedores/nuevo', name: 'ProveedorCrear', component: ProveedorCrear, meta: { requiresAuth: true } },
      { path: 'proveedores/ver/:id', name: 'ProveedorVer', component: () => import('@/views/proveedores/ProveedorVer.vue'), meta: { requiresAuth: true } },
      { path: 'proveedores/editar/:id', name: 'ProveedorEditar', component: () => import('@/views/proveedores/ProveedorEditar.vue'), meta: { requiresAuth: true } },

      // Usuarios Administradores
      { path: 'usuarios', name: 'UsuariosList', component: UsuariosList, meta: { requiresAuth: true } },
      { path: 'usuarios/nuevo', name: 'UsuarioCrear', component: UsuarioCrear, meta: { requiresAuth: true } },
      { path: 'usuarios/roles', name: 'RolesPermisos', component: RolesPermisos, meta: { requiresAuth: true } },

      // Gestión de Módulos
      { path: 'modulos', name: 'ModulosGestion', component: ModulosGestion, meta: { requiresAuth: true } },
      { path: 'modulos/permisos', name: 'ModulosPermisos', component: ModulosPermisos, meta: { requiresAuth: true } },
      { path: 'modulos/activacion', name: 'ModulosActivacion', component: ModulosActivacion, meta: { requiresAuth: true } },
      { path: 'modulos/empresa', name: 'ModulosEmpresaAsignacion', component: ModulosEmpresaAsignacion, meta: { requiresAuth: true } },

      // Facturación del Sistema (suscripciones, pagos)
      { path: 'facturacion/suscripciones', name: 'SuscripcionesList', component: SuscripcionesList, meta: { requiresAuth: true } },
      { path: 'facturacion/pagos', name: 'PagosList', component: PagosList, meta: { requiresAuth: true } },
      { path: 'facturacion/planes', name: 'PlanesList', component: PlanesList, meta: { requiresAuth: true } },
      { path: 'facturacion/planes-sri', name: 'FacturacionPlanes', component: FacturacionPlanes, meta: { requiresAuth: true } },

      // Reportes Centrales
      { path: 'reportes/empresas', name: 'ReportesEmpresas', component: ReportesEmpresas, meta: { requiresAuth: true } },
      { path: 'reportes/ingresos', name: 'ReportesIngresos', component: ReportesIngresos, meta: { requiresAuth: true } },
      { path: 'reportes/uso', name: 'ReportesUso', component: ReportesUso, meta: { requiresAuth: true } },

  // Configuraciones Centrales
  { path: 'configuraciones/usuarios', name: 'AdminConfiguracionUsuarios', component: AdminConfiguracionUsuarios, meta: { requiresAuth: true } },
  { path: 'configuraciones/parametros', name: 'AdminConfiguracionParametros', component: AdminConfiguracionParametros, meta: { requiresAuth: true } },
  { path: 'configuraciones/empresa-principal', name: 'EmpresaPrincipal', component: EmpresaPrincipal, meta: { requiresAuth: true } },
  { path: 'configuraciones/seguridad', name: 'SeguridadDosFactores', component: SeguridadDosFactores, meta: { requiresAuth: true } },
  { path: 'configuraciones/sri', name: 'ConfiguracionSri', component: ConfiguracionSri, meta: { requiresAuth: true } },

  // Control de Acceso (catalogo de modulos, planes y suscripciones)
  { path: 'control-acceso/modulos',       name: 'ControlAccesoModulos',       component: ControlAccesoModulos,       meta: { requiresAuth: true } },
  { path: 'control-acceso/planes',        name: 'ControlAccesoPlanes',        component: ControlAccesoPlanes,        meta: { requiresAuth: true } },
  { path: 'control-acceso/suscripciones', name: 'ControlAccesoSuscripciones', component: ControlAccesoSuscripciones, meta: { requiresAuth: true } },
  { path: 'control-acceso/comprobantes',  name: 'ComprobantesAdmin',          component: ComprobantesAdmin,          meta: { requiresAuth: true } },

  // Notificaciones del sistema
  { path: 'notificaciones', name: 'AdminNotificaciones', component: AdminNotificaciones, meta: { requiresAuth: true } },

  // Infraestructura (Hostinger)
  { path: 'infraestructura', name: 'AdminInfraestructura', component: AdminInfraestructura, meta: { requiresAuth: true } }
    ]
  },

  // Redirección raíz y rutas inválidas => home o inicio de sesión
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Guardia de autenticación con verificación de expiración del JWT
router.beforeEach((to, from, next) => {
  // La navegación inicial en Vue Router 4 tiene from.matched vacío (START_LOCATION).
  // Saltar el check anti-loop en ese caso para que la primera carga siempre funcione.
  const esNavegacionInicial = from.matched.length === 0;

  // Evitar ciclos: misma ruta y misma query (solo en navegaciones posteriores a la inicial)
  if (!esNavegacionInicial && to.path === from.path && JSON.stringify(to.query || {}) === JSON.stringify(from.query || {})) {
    return next(false);
  }

  // Rutas que omiten la guardia de autenticación
  if (to.meta?.omitirGuardia) {
    return next();
  }

  const requiereAutenticacion = to.matched.some(r => r.meta?.requiresAuth);
  const autenticado = estaAutenticado();
  const rutasLogin = new Set(['/iniciar-sesion', '/login', '/iniciar-sesion-prueba', '/depuracion/iniciar-sesion', '/iniciar-sesion-simple']);

  // Ruta protegida sin sesión activa → redirigir al login
  if (requiereAutenticacion && !autenticado) {
    almacen.removeItem('token_acceso_admin');
    almacen.removeItem('token_refresco_admin');
    almacen.removeItem('access_token');
    almacen.removeItem('refresh_token');
    return next({ path: '/iniciar-sesion', query: { redireccion: to.fullPath }, replace: true });
  }

  // Ya autenticado y va al login → redirigir al dashboard
  if (rutasLogin.has(to.path) && autenticado) {
    return next({ path: '/', replace: true });
  }

  return next();
});

export default router
